import { Link } from "@/lib/types/links";
import { AppConfig } from "@/lib/types/apps.types";
import { DeferredLink } from "@/lib/services/linkService/linkService.types";
import { UAParser } from "ua-parser-js";
import { BOT_USER_AGENT_PATTERNS } from "@/const/links";

// The keys App Store Connect puts in its campaign links.
const APP_STORE_CAMPAIGN_PARAMS = ["pt", "ct", "mt"];
const PLAY_STORE_CAMPAIGN_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
];

/**
 * Reserved link parameter carrying an App Store custom product page id, so a
 * link can send visitors to its own product page rather than the default one.
 * It rides in the link's query string instead of a column: that keeps it out of
 * the schema and makes it show up in the per-parameter click breakdown for free.
 */
export const CUSTOM_PRODUCT_PAGE_PARAM = "ppid";

/**
 * Every id the link flow accepts from a URL — link ids, click ids and Apple's
 * custom product page ids are all UUIDs. Shared so the redirect page, the
 * continue route and the store URL builder cannot drift on what they accept.
 */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isIosUserAgent(userAgent: string | null) {
  if (!userAgent) return false;
  const parser = new UAParser(userAgent);
  const { name } = parser.getOS();
  return name === "iOS";
}

/**
 * The three buckets the redirect flow routes on — used by the redirect page and
 * by /api/link/continue, which must agree on where a visitor is sent.
 *
 * Deliberately coarser than ClickPlatform in the click route, which keeps
 * desktop as "unknown" for analytics: here anything that is not a phone or
 * tablet is a web visitor with a web destination.
 */
export function osFromUserAgent(userAgent: string): "ios" | "android" | "web" {
  if (/iPhone|iPad|iPod/i.test(userAgent)) return "ios";
  if (/Android/i.test(userAgent)) return "android";
  return "web";
}

function processRedirectionUrl(
  deferredLink: Pick<
    Link,
    "redirect_url" | "params_pass_strategy" | "specific_params"
  >,
  paramsToPass: URLSearchParams
): string {
  const { redirect_url, params_pass_strategy, specific_params } = deferredLink;

  if (!redirect_url) return "";

  if (params_pass_strategy === "all") {
    try {
      const redirectionUrl = new URL(redirect_url);
      paramsToPass.forEach((value, key) => {
        redirectionUrl.searchParams.set(key, value);
      });
      return redirectionUrl.toString();
    } catch {
      return redirect_url;
    }
  } else if (params_pass_strategy === "specific") {
    try {
      const redirectionUrl = new URL(redirect_url);
      specific_params?.forEach((key) => {
        const value = paramsToPass.get(key);
        if (value !== null) {
          redirectionUrl.searchParams.set(key, value);
        }
      });
      return redirectionUrl.toString();
    } catch {
      return redirect_url;
    }
  } else {
    // strategy "none" or null
    return redirect_url;
  }
}

export function buildWebRedirectionUrl(
  deferredLink: DeferredLink,
  paramsToPass: string
) {
  return processRedirectionUrl(deferredLink, new URLSearchParams(paramsToPass));
}

/**
 * A malformed custom product page id is not a harmless pass-through: the store
 * ignores it and silently serves the default product page, so the campaign
 * looks configured while showing the wrong screenshots. Dropping it here keeps
 * the store URL honest, and the warning keeps a typo distinguishable from a
 * link that was never given a product page at all.
 */
function extractCustomProductPageId(
  searchParams: URLSearchParams
): string | null {
  const value = searchParams.get(CUSTOM_PRODUCT_PAGE_PARAM);
  if (!value) return null;

  if (!UUID_PATTERN.test(value)) {
    console.warn(
      `[link] ignoring malformed ${CUSTOM_PRODUCT_PAGE_PARAM}: ${value.slice(0, 64)}`
    );
    return null;
  }

  return value;
}

function pickQuery(searchParams: URLSearchParams, keys: string[]): string {
  return keys
    .flatMap((key) => {
      const value = searchParams.get(key);
      return value ? [`${key}=${encodeURIComponent(value)}`] : [];
    })
    .join("&");
}

function buildAppStoreQuery(searchParams: URLSearchParams): string {
  const customProductPageId = extractCustomProductPageId(searchParams);

  return [
    customProductPageId &&
      `${CUSTOM_PRODUCT_PAGE_PARAM}=${customProductPageId}`,
    pickQuery(searchParams, APP_STORE_CAMPAIGN_PARAMS),
  ]
    .filter(Boolean)
    .join("&");
}

/**
 * Google Play reads UTM keys only from inside `referrer`.
 *
 * With no click id, the key is left out. If the SDKs find any `click_id`, they
 * skip fingerprint matching, so `click_id=null` would lose the install.
 *
 * The SDKs decode the referrer again, so a UTM value with `&click_id=` in it
 * would pass for a click id. Such values are dropped.
 */
function buildPlayStoreReferrer(
  searchParams: URLSearchParams,
  clickId: string | null
): string {
  const campaignKeys = PLAY_STORE_CAMPAIGN_PARAMS.filter(
    (key) => !searchParams.get(key)?.includes("click_id=")
  );

  return [
    clickId && `click_id=${clickId}`,
    pickQuery(searchParams, campaignKeys),
  ]
    .filter(Boolean)
    .join("&");
}

function buildAppStoreUrl(
  applicationConfig: AppConfig,
  linkSearchParams: string
): string | null {
  const query = buildAppStoreQuery(new URLSearchParams(linkSearchParams));

  return (
    applicationConfig.ios_store_url_override ??
    (applicationConfig.app_store_id
      ? `https://apps.apple.com/app/id${applicationConfig.app_store_id}` +
        (query ? `?${query}` : "")
      : null)
  );
}

export function buildPlayStoreUrl(
  applicationConfig: AppConfig,
  clickId: string | null,
  linkSearchParams: string
): string | null {
  const referrer = buildPlayStoreReferrer(
    new URLSearchParams(linkSearchParams),
    clickId
  );

  return (
    applicationConfig.android_store_url_override ??
    (applicationConfig.package_name
      ? `https://play.google.com/store/apps/details?id=${applicationConfig.package_name}` +
        (referrer ? `&referrer=${encodeURIComponent(referrer)}` : "")
      : null)
  );
}

/**
 * Null when the app has no id for that store — interpolating a missing one
 * yields a real-looking URL (`.../app/idnull`) that dead-ends on a store error
 * page, so callers must be able to tell "no store" from "store here".
 *
 * An override replaces the store URL verbatim and wins over the store id, so an
 * app with no package_name still has somewhere to send Android visitors. Nothing
 * is appended to it: the click id and the store parameters only work on the
 * store's own URL.
 */
export function buildStoreRedirectionUrls(
  applicationConfig: AppConfig,
  clickId: string | null,
  linkSearchParams: string
) {
  return {
    ios: buildAppStoreUrl(applicationConfig, linkSearchParams),
    android: buildPlayStoreUrl(applicationConfig, clickId, linkSearchParams),
  };
}

/**
 * Guards every destination the redirect flow will send a visitor to: anything
 * but http(s) — a `javascript:` URL above all — is refused.
 */
export function isHttpUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Hands the click id from /api/link/click to /api/link/continue out of band.
 * The App Preview CTA is a plain link inside a script-less iframe rendered
 * before the click request resolves, so its href cannot carry the id — the
 * cookie can. Short-lived: it only has to survive a store round-trip.
 */
export const APP_PREVIEW_CLICK_COOKIE_MAX_AGE_SECONDS = 600;

export function appPreviewClickCookieName(linkId: string): string {
  return `detour_click_${linkId}`;
}

type HeadersLike = {
  get(name: string): string | null;
};

export function buildLinkOrigin(headers: HeadersLike): string {
  const protocol = headers.get("x-forwarded-proto") ?? "https";
  const host = headers.get("host") ?? "";
  return `${protocol}://${host}`;
}

// Origin plus the first path segment. `generateMetadata` and the page must
// build the same string, because the cached link read is keyed on it.
export function buildCanonicalUrlSegment(
  headers: HeadersLike,
  slug: string[]
): string {
  const origin = buildLinkOrigin(headers);
  return slug.length > 0 ? `${origin}/${slug[0]}` : origin;
}

export function getSearchParamsFromPath(pathWithParams: string): string {
  if (!pathWithParams) return "";
  try {
    return new URL(pathWithParams, "http://localhost").searchParams.toString();
  } catch {
    return "";
  }
}

/**
 * Adds the query a visitor arrived with to the query a short link stores.
 *
 * A short link's URL gets more parameters on its way to the visitor: Facebook
 * appends fbclid, ad campaigns append utm_*, and a marketer may append ppid or
 * the App Store's pt/ct. A plain app link keeps them because it is resolved
 * from the URL as tapped; a short link is resolved from its stored parameters,
 * so without this they never reach the stores, the click or the SDK.
 *
 * A key present in both takes the incoming values, all of them, the same as
 * a plain app link would.
 *
 * @param storedSearch - The short link's own query, with or without "?"
 * @param incomingSearch - The query of the URL the visitor opened, with or without "?"
 * @returns The merged query without a leading "?" ("" when both are empty)
 */
export function mergeIncomingQuery(
  storedSearch: string,
  incomingSearch: string
): string {
  const merged = new URLSearchParams(storedSearch);
  const incoming = new URLSearchParams(incomingSearch);
  incoming.forEach((_value, key) => merged.delete(key));
  incoming.forEach((value, key) => merged.append(key, value));
  return merged.toString();
}

export function resolveDeferredLinkUrl(
  baseUrl: string,
  pathWithParams: string
): string {
  if (!baseUrl || !pathWithParams) return baseUrl;
  try {
    const base = new URL(baseUrl);
    return new URL(pathWithParams, base.origin).toString();
  } catch {
    return baseUrl;
  }
}

export function getPathWithSearchFromUrl(
  urlString: string | null,
  fallback: string
): string {
  if (!urlString) return fallback;
  try {
    const url = new URL(urlString);
    return url.pathname + url.search;
  } catch {
    return fallback;
  }
}

function stripPort(domain: string) {
  return domain.split(":")[0];
}

export function resolveLinkHost() {
  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  if (!hostname) return "";

  const host = stripPort(hostname);
  const parts = host.split(".");
  const tld = parts[parts.length - 1];

  if (tld === "link") return host;

  // Handle staging environment
  if (tld === "app") {
    parts[parts.length - 1] = "link";
    parts.splice(parts.length - 2, 0, "staging");
    return parts.join(".");
  }

  // Handle prod environment
  if (tld === "dev") {
    parts[parts.length - 1] = "link";
    return parts.join(".");
  }

  return host;
}

function decodeQueryValue(value: string) {
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
}

export function encodeQueryParamValues(input: string) {
  if (!input) return input;

  let normalized = input;
  if (!normalized.startsWith("?") && !normalized.startsWith("/")) {
    normalized = `?${normalized}`;
  }

  let path = "";
  let query = "";
  if (normalized.startsWith("/")) {
    const [pathPart, queryPart] = normalized.split("?", 2);
    path = pathPart;
    query = queryPart ?? "";
  } else if (normalized.startsWith("?")) {
    query = normalized.slice(1);
  }

  if (!query) return normalized;

  const encodedQuery = query
    .split("&")
    .map((pair) => {
      if (!pair) return pair;
      const eqIndex = pair.indexOf("=");
      if (eqIndex === -1) return pair;

      const key = pair.slice(0, eqIndex);
      const value = pair.slice(eqIndex + 1);
      const decodedValue = decodeQueryValue(value);
      const encodedValue = encodeURIComponent(decodedValue);
      return `${key}=${encodedValue}`;
    })
    .join("&");

  if (normalized.startsWith("/")) {
    return `${path}?${encodedQuery}`;
  }

  return `?${encodedQuery}`;
}

export function isBot(userAgent: string): boolean {
  return BOT_USER_AGENT_PATTERNS.some((pattern) => pattern.test(userAgent));
}
